/**
 * i18next setup (frontend.md §5.1–§5.2).
 *
 * The load-bearing detail is at the bottom: `mus` is **one** resource object
 * registered for both locales, so there is no English `mus.json` that could ever
 * be translated. Drift is not policed, it is structurally impossible.
 */

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import type { I18nNamespace } from '@ordago/shared';

import enAdmin from './locales/en/admin.json';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enGame from './locales/en/game.json';
import enLobby from './locales/en/lobby.json';
import enSocial from './locales/en/social.json';
import esAdmin from './locales/es/admin.json';
import esAuth from './locales/es/auth.json';
import esCommon from './locales/es/common.json';
import esErrors from './locales/es/errors.json';
import esGame from './locales/es/game.json';
import esLobby from './locales/es/lobby.json';
import esSocial from './locales/es/social.json';
import mus from './locales/mus.json';

export const LOCALE_STORAGE_KEY = 'ordago.locale';

const resources = {
  es: {
    common: esCommon,
    auth: esAuth,
    errors: esErrors,
    lobby: esLobby,
    game: esGame,
    admin: esAdmin,
    social: esSocial,
    mus,
  },
  // Same `mus` object reference, deliberately (frontend.md §5.4).
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    lobby: enLobby,
    game: enGame,
    admin: enAdmin,
    social: enSocial,
    mus,
  },
} as const;

/**
 * Every namespace `I18N_NAMESPACES` declares must actually be registered.
 *
 * This is the check that was missing while `admin` sat in that list for two
 * milestones with no file in either locale: `check-i18n` compares es to en, so
 * a namespace absent from both is invisible to it, and nothing else looked.
 * Asserting the real `resources.es` — not a type of it — means the file has to
 * exist and be imported, not merely be declared somewhere.
 *
 * `mus` is deliberately not in `I18N_NAMESPACES` (§5.4) and rides along as an
 * extra key, which a `Record` assignment from a reference permits.
 */
const everyNamespaceRegistered: Record<I18nNamespace, unknown> = resources.es;
void everyNamespaceRegistered;

void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    supportedLngs: ['es', 'en'],
    // es-AR and es-MX fold to es; en-GB folds to en.
    nonExplicitSupportedLngs: true,
    ns: ['common', 'mus'],
    defaultNS: 'common',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

function syncDocumentLang(lng: string): void {
  document.documentElement.lang = lng;
}

syncDocumentLang(i18next.resolvedLanguage ?? 'es');
i18next.on('languageChanged', syncDocumentLang);

export { i18next };
